import * as Fmt from "./format";
import type { ModelResult, Scenario } from "./types";
import {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "./decision-thresholds";
import { capacityDisclosure } from "./presentation/capacity-view-model";
export {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "./decision-thresholds";

export interface DealRisk {
  id: string;
  name: string;
  likelihood: "Low" | "Medium" | "High";
  impact: "Low" | "Medium" | "High";
  proposedOwner: string;
  dueGate: string;
  currentControl: string;
  residualRating: "Low" | "Medium" | "High";
  escalationTrigger: string;
  mitigation: string;
  status: "Proposed — not agreed or executed";
}

function residualRating(
  likelihood: DealRisk["likelihood"],
  impact: DealRisk["impact"],
): DealRisk["residualRating"] {
  if (likelihood === "High" && impact === "High") return "High";
  if (likelihood === "Low" && impact === "Low") return "Low";
  return "Medium";
}

/** Scenario-derived decision risks. Controls are deliberately framed as actions
 * to verify or negotiate, never as completed contractual commitments. */
export function buildDealRisks(
  scenario: Scenario,
  result: ModelResult,
): DealRisk[] {
  const a = scenario.assumptions;
  const k = result.kpis;
  const y1 = result.years[0];
  const capacity = capacityDisclosure(scenario);
  const capacityGap =
    (capacity.planningTarget ?? capacity.scheduledClassroomSeats) -
    capacity.scheduledClassroomSeats;
  const rentBurden = k.rentPctRevenueYear1;
  const rentLikelihood: DealRisk["likelihood"] =
    rentBurden > OPERATOR_AFFORDABILITY_CEILING
      ? "High"
      : rentBurden > OPERATOR_AFFORDABILITY_CAUTION
        ? "Medium"
        : "Low";
  const escalationBasis =
    a.rentEscalationMethod === "cpi"
      ? `Rent growth is linked to an assumed CPI of ${Fmt.pct(a.cpiAssumption ?? 0.03, 1)}`
      : a.rentEscalationMethod === "market"
        ? `Market review growth is assumed at ${Fmt.pct(a.marketReviewPct ?? 0.04, 1)}`
        : `Fixed annual rent indexation is ${Fmt.pct(a.rentIndexationPct, 1)}`;
  const risk = (item: Omit<DealRisk, "residualRating">): DealRisk => ({
    ...item,
    residualRating: residualRating(item.likelihood, item.impact),
  });
  const undatedGate = (gate: string) =>
    `Target date: TBD — Board Secretariat to set; ${gate}`;

  return [
    risk({
      id: "R-01",
      name: "Enrolment ramp / opening demand",
      likelihood: y1 && y1.utilisation < 0.6 ? "High" : "Medium",
      impact: "High",
      proposedOwner: "Reddam CFO / Head of Admissions",
      dueGate: undatedGate(
        "before Board approval and at the pre-opening go/no-go gate",
      ),
      currentControl: `${a.rampYears}-year modelled ramp with Y1 utilisation at ${Fmt.pct(y1?.utilisation ?? 0, 0)}; deposits, funded pipeline evidence and documented same-site downside/upside scenarios are not loaded.`,
      escalationTrigger:
        "Deposited enrolments fall below the Board-approved opening threshold or forecast ramp funding increases.",
      mitigation: `Modelled Y1 utilisation is ${Fmt.pct(y1?.utilisation ?? 0, 0)} with a ${a.rampYears}-year ramp. Verify deposits and funded marketing, then approve operator-led Mac Park downside and upside cases with explicit assumption deltas before commitment.`,
      status: "Proposed — not agreed or executed",
    }),
    ...(capacity.hasPlanningOverride && capacityGap > 0
      ? [
          risk({
            id: "R-02",
            name: "Capacity evidence / planning override",
            likelihood: "Medium",
            impact: "High",
            proposedOwner: "Education Lead / Property Director",
            dueGate: undatedGate("before Board approval and lease execution"),
            currentControl: `${Fmt.num(capacity.planningTarget ?? 0, 0)} planning target overrides ${Fmt.num(capacity.scheduledClassroomSeats, 0)} scheduled classroom seats; no teaching-group, timetable, specialist-space, staging, egress or amenities reconciliation is loaded.`,
            escalationTrigger: `${Fmt.num(capacityGap, 0)}-seat planning gap remains unsupported, or building/operational capacity constrains the Board-approved enrolment plan.`,
            mitigation:
              "Reconcile teaching groups, class sizes, timetable utilisation, specialist/shared spaces, staging, egress and amenities, with joint Education Lead / Property Director sign-off.",
            status: "Proposed — not agreed or executed",
          }),
        ]
      : []),
    risk({
      id: "R-03",
      name: "Fee growth below plan",
      likelihood: "Medium",
      impact: "High",
      proposedOwner: "Reddam CFO / Commercial Director",
      dueGate: undatedGate("before Board approval and each annual budget"),
      currentControl: `Scenario applies ${Fmt.pct(a.tuitionGrowthPct, 1)} tuition growth; an independently benchmarked fee case is not loaded.`,
      escalationTrigger:
        "Approved fee growth is below the model assumption or affordability testing weakens the planned fee path.",
      mitigation: `Tuition growth is an unverified ${Fmt.pct(a.tuitionGrowthPct, 1)} model assumption. Benchmark fees and test CPI-only and flat-fee cases; no fee cap is represented as contracted.`,
      status: "Proposed — not agreed or executed",
    }),
    risk({
      id: "R-04",
      name: `Occupancy cost (${Fmt.pct(rentBurden, 0)} of Y1 revenue)`,
      likelihood: rentLikelihood,
      impact: "High",
      proposedOwner: "Reddam CFO / Property Director",
      dueGate: undatedGate("before lease execution and each annual budget"),
      currentControl: `Model reports Y1 occupancy cost at ${Fmt.pct(rentBurden, 0)} of revenue and peak funding at ${Fmt.compactCurrency(k.reservesRequiredToSurvive)}; no executed relief mechanism is evidenced.`,
      escalationTrigger:
        "Modelled occupancy cost exceeds the approved limit, opening liquidity rises, or lease relief is not executed.",
      mitigation: `${escalationBasis}. The model shows ${k.negativeEbitdaYears} EBITDA-negative year${k.negativeEbitdaYears === 1 ? "" : "s"} and ${Fmt.compactCurrency(k.reservesRequiredToSurvive)} peak funding need; negotiate rent relief only through an executed lease amendment.`,
      status: "Proposed — not agreed or executed",
    }),
    risk({
      id: "R-05",
      name: "Construction cost / scope overrun",
      likelihood: "Medium",
      impact: "High",
      proposedOwner: "Developer PM / Reddam Property Director",
      dueGate: undatedGate(
        "before construction contract and Board-approved cost budget",
      ),
      currentControl: `${Fmt.pct(a.developerContingencyRate, 1)} modelled contingency; independent QS reconciliation, scope closure and contract status are not loaded.`,
      escalationTrigger:
        "Tender reconciliation exceeds the approved cost envelope, exclusions remain open, or contingency is consumed before completion.",
      mitigation: `The model carries ${Fmt.pct(a.developerContingencyRate, 1)} contingency on hard construction. Obtain an independent QS reconciliation, close exclusions, and execute fixed-price/open-book protections; none is assumed to be in place.`,
      status: "Proposed — not agreed or executed",
    }),
    risk({
      id: "R-06",
      name: "Planning delay / refusal",
      likelihood: "Medium",
      impact: "High",
      proposedOwner: "Developer Planning Lead / Reddam Property Director",
      dueGate: undatedGate("before unconditional lease commitment"),
      currentControl: `${a.developmentMonths}-month modelled development period; consent pathway, status and programme evidence are not loaded.`,
      escalationTrigger:
        "Consent critical path moves beyond the approved opening programme or a material condition changes cost or school operations.",
      mitigation: `Approval status is not modelled. Confirm the consent pathway and programme before commitment; the financial model assumes a ${a.developmentMonths}-month development period.`,
      status: "Proposed — not agreed or executed",
    }),
    risk({
      id: "R-07",
      name: "Interest-rate / refinancing exposure",
      likelihood: "Medium",
      impact: "High",
      proposedOwner: "Developer CFO / Reddam CFO",
      dueGate: undatedGate("before financial close and refinancing approval"),
      currentControl: `${Fmt.pct(a.holdingInterestRate, 1)} base debt-rate assumption; lender term sheet, refinance headroom and hedge evidence are not loaded.`,
      escalationTrigger:
        "Debt pricing, facility capacity or refinance proceeds move outside the approved counterparty case.",
      mitigation: `Base debt rate is an assumed ${Fmt.pct(a.holdingInterestRate, 1)} before margins. Confirm lender terms, refinancing headroom and any hedge in writing; the model does not evidence a swap.`,
      status: "Proposed — not agreed or executed",
    }),
  ];
}
